import { PropertyTaxonomy, PropertyTaxonomyKeyDef, PropertyTaxonomyNode } from "../models/site.model";
import { PropertyValue } from "../models/metadata.model";

/** Unicode NFC normalize a custom-property key string. */
export function normalizePropertyKey(value: string): string {
    return value.normalize("NFC");
}

export type PropertyKeyValidationResult =
    | { ok: true; value: string }
    | { ok: false; message: string };

const KEY_FORMAT = /^[A-Za-z0-9_-]+$/;

/**
 * Validates a new custom-property key write.
 * Empty keys disallowed; only alphanumerals, underscore, and dash (case-sensitive).
 */
export function validatePropertyKey(raw: string): PropertyKeyValidationResult {
    const value = normalizePropertyKey(raw);
    if (value === "") {
        return { ok: false, message: "Key must not be empty." };
    }
    if (!KEY_FORMAT.test(value)) {
        return {
            ok: false,
            message: "Use only letters, numbers, underscore, and dash.",
        };
    }
    return { ok: true, value };
}

/**
 * Acceptable key identity in a Site.propertyTaxonomies graph.
 * New keys must match KEY_FORMAT. Legacy #2878 migrated keys may break KEY_FORMAT
 * (clarification 2) but must be non-empty and must not contain ':' (never a legal segment).
 */
export function isAcceptableTaxonomyGraphKey(raw: string): boolean {
    const value = normalizePropertyKey(raw);
    if (value === "") {
        return false;
    }
    if (KEY_FORMAT.test(value)) {
        return true;
    }
    return !value.includes(":");
}

/** Presentation label for a key; falls back to the key when the language has no label (AC-002). */
export function labelForPropertyKey(
    taxonomy: PropertyTaxonomy | undefined,
    key: string,
    lang: string | undefined,
): string {
    if (!taxonomy || !lang) {
        return key;
    }
    const label = taxonomy.keys[key]?.labels?.[lang];
    return label && label.length > 0 ? label : key;
}

/** Collect descendant keys of `key` via `children` (does not include `key` itself). */
export function collectDescendantKeys(taxonomy: PropertyTaxonomy, key: string): string[] {
    const result: string[] = [];
    const seen = new Set<string>();
    const stack = [...(taxonomy.keys[key]?.children ?? [])];
    while (stack.length > 0) {
        const current = stack.pop()!;
        if (seen.has(current)) {
            continue;
        }
        seen.add(current);
        result.push(current);
        const children = taxonomy.keys[current]?.children;
        if (children?.length) {
            for (const child of children) {
                stack.push(child);
            }
        }
    }
    return result;
}

/**
 * Expand a filter term to `{ term } ∪ descendants` when the term is a taxonomy key;
 * otherwise exact term only (AC-008).
 */
export function expandTaxonomyFilterKeys(
    taxonomy: PropertyTaxonomy | undefined,
    term: string,
): string[] {
    const normalized = normalizePropertyKey(term);
    if (!taxonomy?.keys[normalized]) {
        return [normalized];
    }
    return [normalized, ...collectDescendantKeys(taxonomy, normalized)];
}

/**
 * Mongo clause for one property field: OR across filter terms via key-set expansion then `$in`.
 * Non-strings stay exact. Without a taxonomy, string terms match exactly only.
 */
export function buildPropertyKeySetMatch(
    fieldPath: string,
    terms: PropertyValue[],
    taxonomy?: PropertyTaxonomy,
): Record<string, unknown> {
    const expanded = new Set<PropertyValue>();
    for (const term of terms) {
        if (typeof term === "string") {
            for (const key of expandTaxonomyFilterKeys(taxonomy, term)) {
                expanded.add(key);
            }
        } else {
            expanded.add(term);
        }
    }
    return { [fieldPath]: { $in: Array.from(expanded) } };
}

/** True when stored string equals the filter term or any expanded descendant key under the taxonomy. */
export function propertyKeyMatchesFilterTerm(
    stored: string,
    term: string,
    taxonomy?: PropertyTaxonomy,
): boolean {
    const expanded = expandTaxonomyFilterKeys(taxonomy, term);
    const normalizedStored = normalizePropertyKey(stored);
    return expanded.includes(normalizedStored);
}

function unionChildren(existing: string[], incoming: string[]): string[] {
    const result = [...existing];
    const seen = new Set(existing);
    for (const child of incoming) {
        if (!seen.has(child)) {
            seen.add(child);
            result.push(child);
        }
    }
    return result;
}

function ensureKey(keys: Record<string, PropertyTaxonomyKeyDef>, key: string): PropertyTaxonomyKeyDef {
    if (!keys[key]) {
        keys[key] = { children: [] };
    }
    return keys[key];
}

/** Walk a legacy #2878 segment tree into a key DAG (merge duplicate segments). */
function ingestLegacyNodes(
    nodes: PropertyTaxonomyNode[],
    keys: Record<string, PropertyTaxonomyKeyDef>,
    parentChildren: string[],
): void {
    for (const node of nodes) {
        const key = normalizePropertyKey(node.value);
        const def = ensureKey(keys, key);
        if (!parentChildren.includes(key)) {
            parentChildren.push(key);
        }
        const childKeys: string[] = [];
        if (node.values?.length) {
            ingestLegacyNodes(node.values, keys, childKeys);
        }
        def.children = unionChildren(def.children, childKeys);
    }
}

function isNewShape(raw: unknown): raw is PropertyTaxonomy {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return false;
    }
    const candidate = raw as PropertyTaxonomy;
    return Array.isArray(candidate.roots) && candidate.keys !== undefined && typeof candidate.keys === "object";
}

/**
 * Normalize legacy segment trees or new DAG shapes into `PropertyTaxonomy`.
 * Duplicate legacy segment values merge; children are unioned (first-seen order).
 */
export function normalizePropertyTaxonomy(raw: unknown): PropertyTaxonomy {
    if (isNewShape(raw)) {
        const keys: Record<string, PropertyTaxonomyKeyDef> = {};
        for (const [key, def] of Object.entries(raw.keys ?? {})) {
            const normalizedKey = normalizePropertyKey(key);
            const labels: Record<string, string> | undefined = def?.labels
                ? Object.fromEntries(
                    Object.entries(def.labels)
                        .filter(([, label]) => typeof label === "string" && label.length > 0)
                        .map(([lang, label]) => [lang, label]),
                )
                : undefined;
            keys[normalizedKey] = {
                ...(labels && Object.keys(labels).length > 0 ? { labels } : {}),
                children: Array.isArray(def?.children)
                    ? def.children.map((child) => normalizePropertyKey(String(child)))
                    : [],
            };
        }
        return {
            keys,
            roots: (raw.roots ?? []).map((root) => normalizePropertyKey(String(root))),
        };
    }

    const keys: Record<string, PropertyTaxonomyKeyDef> = {};
    const roots: string[] = [];
    if (Array.isArray(raw)) {
        ingestLegacyNodes(raw as PropertyTaxonomyNode[], keys, roots);
    }
    return { keys, roots };
}

/** Normalize every entry in a site's propertyTaxonomies map. */
export function normalizePropertyTaxonomiesMap(
    raw: Record<string, unknown> | undefined | null,
): Record<string, PropertyTaxonomy> {
    if (!raw) {
        return {};
    }
    const result: Record<string, PropertyTaxonomy> = {};
    for (const [name, value] of Object.entries(raw)) {
        result[name] = normalizePropertyTaxonomy(value);
    }
    return result;
}

/** Directed-graph cycle check over all `children` edges (AC-004). */
export function taxonomyHasCycle(taxonomy: PropertyTaxonomy): boolean {
    const visiting = new Set<string>();
    const visited = new Set<string>();

    const visit = (key: string): boolean => {
        if (visited.has(key)) {
            return false;
        }
        if (visiting.has(key)) {
            return true;
        }
        visiting.add(key);
        for (const child of taxonomy.keys[key]?.children ?? []) {
            if (visit(child)) {
                return true;
            }
        }
        visiting.delete(key);
        visited.add(key);
        return false;
    };

    for (const key of Object.keys(taxonomy.keys)) {
        if (visit(key)) {
            return true;
        }
    }
    return false;
}

/**
 * True if attaching `childKey` under `parentKey` (null = root) would create a cycle.
 * Does not mutate `taxonomy`.
 */
export function wouldIntroduceCycle(
    taxonomy: PropertyTaxonomy,
    parentKey: string | null,
    childKey: string,
): boolean {
    if (parentKey === null) {
        return false;
    }
    if (parentKey === childKey) {
        return true;
    }
    const draft: PropertyTaxonomy = {
        roots: [...taxonomy.roots],
        keys: Object.fromEntries(
            Object.entries(taxonomy.keys).map(([key, def]) => [
                key,
                { ...def, children: [...def.children], labels: def.labels ? { ...def.labels } : undefined },
            ]),
        ),
    };
    ensureKey(draft.keys, childKey);
    const parent = ensureKey(draft.keys, parentKey);
    if (!parent.children.includes(childKey)) {
        parent.children = [...parent.children, childKey];
    }
    return taxonomyHasCycle(draft);
}

/**
 * Normalize string values in a properties map for new key writes.
 * Mutates `properties` in place when all string values are valid.
 */
export function normalizePropertiesMapInPlace(
    properties: Record<string, PropertyValue[]>,
): string[] {
    const errors: string[] = [];
    const normalized: Record<string, PropertyValue[]> = {};
    for (const [name, values] of Object.entries(properties)) {
        const next: PropertyValue[] = [];
        for (const value of values) {
            if (typeof value === "string") {
                const result = validatePropertyKey(value);
                if (!result.ok) {
                    errors.push(`Invalid key for "${name}": ${result.message}`);
                } else {
                    next.push(result.value);
                }
            } else {
                next.push(value);
            }
        }
        normalized[name] = next;
    }
    if (errors.length === 0) {
        for (const name of Object.keys(properties)) {
            properties[name] = normalized[name];
        }
        for (const name of Object.keys(normalized)) {
            if (!(name in properties)) {
                properties[name] = normalized[name];
            }
        }
    }
    return errors;
}
